import {
  DTOSegmentForMcView,
  DTOSegmentGroupMetricResponse,
  DTOSegmentGroupResponse,
  DTOSegmentTemplateResponse,
} from "vue-app/api/dto";

/**
 * This comes from the backend API and should be always be sent to the backend in this format.
 * We should use this enum everywhere the subscription status is used.
 */
export enum SubscriptionStatusType {
  Any,
  Subscribed,
  Unsubscribed,
  Unknown,
  UnsubscribedAndUnknown,
}

export enum SendSubscriptionStatusType {
  Subscribers = 1,
  Unknown = 2,
  SubscribersAndUnknown = 4,
}

export enum EngagedTypeId {
  Engaged = 1,
  Unengaged = 2,
  EngagedAndUnengaged = 3,
}

export enum EngagementStatusId {
  CREATED = 0,
  PAUSED = 1,
  ACTIVE = 2,
  TERMINATED = 3,
  SCHEDULED = 4,
  COMPLETED = 5,
}

// Common/src/main/java/me/edrone/common/model/CustomerListType.java
export enum CustomerListTypeId {
  All = 0,
  Tagged = 8,
  Category = 13,
  CustomStatic = 14,
  ResendOpened = 26,
  ResendNotOpened = 27,
  CustomFilterByCustomerFields = 29,
  Mock = 30,
  AllByEngagedType = 32,
  Segment = 33,
  Freemium = 34,
  ExternallySegmentedCustomers = 35,
}

/** TODO this should be replaced to use the enum SubscriptionStatusType everywhere
 *  https://edrone.atlassian.net/browse/COOLD-1064
 * */
export type SubscriptionStatus = "SUBSCRIBED" | "UNSUBSCRIBED" | "UNKNOWN";

/** TODO this should be replaced to use the enum SubscriptionStatusType everywhere
 *  https://edrone.atlassian.net/browse/COOLD-1064
 * */
export const SUBSCRIPTION_STATUS_ID = {
  SUBSCRIBED: "SUBSCRIBED",
  UNSUBSCRIBED: "UNSUBSCRIBED",
  UNKNOWN: "UNKNOWN",
} as { [key in SubscriptionStatus]: SubscriptionStatus };

export const ENGAGEMENT_TYPES = {
  RESTORE_CUSTOMERS: 1,
  LOYALTY_PROGRAM: 2,
  BIGGEST_SPENDERS: 3,
  FREQUENT_BUYERS: 4,
  ABANDONED_CARTS: 5,
  /**
   * Legacy Newsletter. Used before Newsletter hub.
   */
  NEWSLETTER: 6,
  ADVANCED_RESTORE_CUSTOMERS: 7,
  SMS_NEWSLETTER: 8,
  AFTER_SALE: 9,
  NEWSLETTER_SUBSCRIPTION: 10,
  CUSTOM_NEWSLETTER: 11,
  WISH_LIST: 12,
  NEWSLETTER_AB: 13,
  NEWSLETTER_CATEGORIES: 14,
  RECOMMENDATIONS: 15,
  CROSS_SELLING_TRANSACTIONAL: 16,
  PUSH_NEWSLETTER: 17,
  CHAT_SUBSCRIPTION: 18,
  BIRTHDAY: 19,
  REFER_A_FRIEND: 20,
  CUSTOM_FLOWS: 21,
  NEWSLETTER_HUB: 22,
  NEWSLETTER_SIGN_UP_AFTER_PURCHASE: 23,
  PRICE_CHANGE_UP: 24,
  PRICE_CHANGE_DOWN: 25,
  LOW_STOCK_PRODUCTS: 26,
  NEWEST_PRODUCTS: 27,
  ASK_FOR_REVIEW: 28,
  BACK_IN_STOCK: 29,
  WHATSAPP_NEWSLETTER: 30,
  SMS_SUBSCRIPTION: 31,
  RECOVER_UNPAID_ORDER: 32,
} as const;

export type ENGAGEMENT_TYPE_ID = (typeof ENGAGEMENT_TYPES)[keyof typeof ENGAGEMENT_TYPES];

export enum CHANNEL_TYPE {
  UNRECOGNIZED = -1,
  EMAIL = 1,
  SMS = 2,
  EMAIL_AND_SMS = 3,
  PUSH = 4,
  WHATSAPP = 16,
}

export const CHANNEL_TYPE_NAMES = {
  [CHANNEL_TYPE.EMAIL]: "Email",
  [CHANNEL_TYPE.SMS]: "SMS",
  [CHANNEL_TYPE.EMAIL_AND_SMS]: "Email & SMS",
  [CHANNEL_TYPE.PUSH]: "Push",
  [CHANNEL_TYPE.WHATSAPP]: "WhatsApp",
} as const;

export const OPT_IN_TYPES = {
  DOUBLE_OPT_IN: 1,
  LEGACY_DOUBLE_OPT_IN_NON_EDRONE: 2,
  LEGACY_SINGLE_OPT_IN: 3,
  SINGLE_OPT_IN: 4,
} as const;

export const LEGACY_ENGAGEMENT_TYPE_ID = [6, 11, 14] as const;

export type SegmentWithMetrics = DTOSegmentForMcView & DTOSegmentGroupMetricResponse;
export type SegmentsGroupsWithMetrics = DTOSegmentGroupResponse & {
  segments: SegmentWithMetrics[];
};

export type TemplatesWithMetrics = DTOSegmentTemplateResponse & DTOSegmentGroupMetricResponse;

export type CharData = {
  datasets: { label: string; data: { key: string; value: number }[] }[];
  labels: string[];
};

export enum ORDERS_FILTERS {
  all = 0,
  opened = 65, // 65 = 64 (whatsappOpened) + 1 (emailOpened)
  coupon = 2, // legacy, not used, but we have historical data with this value
  clicked = 148, // 148 = 128 (whatsappClicked) + 16 (smsClicked) + 4 (emailClicked)
  delivered = 40, // 40 = 32 (whatsappDelivered) + 8 (smsDelivered)
}

export enum ORDER_INDEX {
  CONFIRMATION_MESSAGE = 0,
  WELCOME_MESSAGE = 1,
  INTRODUCTION_MESSAGE_1 = 2,
  INTRODUCTION_MESSAGE_2 = 3,
  INTRODUCTION_MESSAGE_3 = 4,
  INTRODUCTION_MESSAGE_4 = 5,
}

export const COUNT_OF_INTRODUCTION_MESSAGES = 4;

export enum MAX_INPUT_LENGTH {
  SENDER = 64,
  SUBJECT = 128,
}

export enum ENGAGEMENT_STATUS {
  CREATED = "CREATED",
  PAUSED = "PAUSED",
  ACTIVE = "ACTIVE",
  TERMINATED = "TERMINATED",
  SCHEDULED = "SCHEDULED",
  COMPLETED = "COMPLETED",
}

export enum ENGAGEMENT_STATUS_ID {
  CREATED = 0,
  PAUSED = 1,
  ACTIVE = 2,
  TERMINATED = 3,
  SCHEDULED = 4,
  COMPLETED = 5,
}

export const AGENT_TRACKER_ROLE = {
  ROLE_INBOX: "ROLE_INBOX",
  ROLE_DB: "ROLE_DB",
  ROLE_SETTINGS: "ROLE_SETTINGS",
  ROLE_ENGAGEMENT: "ROLE_ENGAGEMENT",
  ROLE_ONSITE: "ROLE_ONSITE",
  ROLE_ADVERTISEMENT: "ROLE_ADVERTISEMENT",
  ROLE_REPORTS: "ROLE_REPORTS",
  ROLE_CRM: "ROLE_CRM",
  ROLE_LOGS: "ROLE_LOGS",
  ROLE_PERMISSIONS: "ROLE_PERMISSIONS",
  ROLE_AGENT: "ROLE_AGENT",
  ROLE_SUPER_AGENT: "ROLE_SUPER_AGENT",
} as const;

export enum SUMMARY_STATE {
  EMPTY = "empty",
  LOADING = "loading",
  INFO = "info",
}

export const OTHER_PROVIDER_QUESTION_ID = 15 as const;

export enum CATEGORY_TYPE {
  SMART_CATEGORY = "smart_category",
  YOUR_CATEGORY = "your_category",
}

export enum COUNTRY_ID {
  POLAND = 1,
  BRAZIL = 2,
}

export const DEFAULT_TOOLBAR_ITEMS = [
  "fontColor",
  "fontFamily",
  "fontSize",
  "fontBackgroundColor",
  "lineHeight",
  "bold",
  "italic",
  "underline",
  "link",
  "alignment:left",
  "alignment:center",
  "alignment:right",
  "alignment:justify",
];

// code/Common/src/main/java/me/edrone/common/model/onsite/OnsiteStatus.java
export enum ONSITE_STATUS {
  INACTIVE = 0,
  ACTIVE = 1,
  DELETED = 2,
}

export enum SORT_DIRECTION {
  ASC = "asc",
  DESC = "desc",
}

export enum PRODUCT_GROUP {
  NONE = "none",
  YOUR_CATEGORIES = "your_categories",
}

export const EDRONE_SENDING_DOMAIN = "edrone.io" as const;

export enum SMS_LENGTH_LIMIT {
  SPECIAL = 70,
  NORMAL = 160,
  SPECIAL_AFTER = 67,
  NORMAL_AFTER = 153,
}

export enum AllegroIntegrationStatus {
  INTEGRATED = 1,
  TOKEN_EXPIRED = 2,
  DELETED = 3,
  DISABLED = 4,
}

export enum OnsiteTypeId {
  SOCIAL_PROOF = 3,
  SMART_RECOMMENDATIONS = 9,
}

export const TOP_BAR_HEIGHT = 3.5 as const; // in rem
export const PAYMENT_BAR_HEIGHT = 4 as const; // in rem
export const INTEGRATION_BANNER_HEIGHT = 2.75 as const; // in rem
export const INTEGRATION_INCOMPLETE_BAR_HEIGHT = 4 as const; // in rem

export enum BLOCKED_READY_TO_RUN_PLATFORMS {
  NUVEMSHOP = "nuvemshop",
  SHOPER = "shoper",
}

export enum PLATFORMS {
  SHOPER = "shoper",
  NUVEMSHOP = "nuvemshop",
  SHOPIFY = "shopify",
  TRAY = "tray",
  LOJAINTEGRADA = "lojaintegrada",
  PRESTASHOP = "prestashop",
  WOOCOMMERCE = "woocommerce",
  IDOSELL = "iai",
}

export const PLATFORMS_WITH_PACKAGE_DOWNLOAD: PLATFORMS[] = [PLATFORMS.PRESTASHOP, PLATFORMS.WOOCOMMERCE];

export const FREEMIUM = {
  CONTACTS: 200,
  MESSAGES: 200,
};

export enum ORDER_STATUS_FILTER {
  ALL = "all",
  PLACED = "placed",
  CANCELLED = "cancelled",
}

export const IntegrationChannelKeys = ["email", "inbox", "facebook", "whatsapp", "allegro"] as const;
// AutoresponderChannelKeys are used in the autoresponder settings. Must match the keys used in the backend.
export const AutoresponderChannelKeys = ["Email", "Chat", "Whatsapp", "Messenger", "Allegro"] as const;

export enum SMS_VARIABLE {
  UNSUBSCRIBE_LINK = "*|UNSUBSCRIBE_LINK|*",
  SUBSCRIBE_LINK = "*|SUBSCRIBE_LINK|*",
  FIRST_NAME = "*|FIRST_NAME|*",
  LAST_NAME = "*|LAST_NAME|*",
  COUPON = "*|COUPON|*",
  PRODUCT_URL = "*|PRODUCT_URL|*",
}

export enum ONSITE_TYPE {
  POPUP = 1,
  WEBLAYER = 2,
  SOCIAL_PROOF = 3,
  OTHER = 4,
  POPUP_V2 = 5,
  AVA = 6,
  BANNER = 7,
  SLIDER = 8,
  SMART_CROSS_SELLING = 9,
  SMART_CROSS_SELLING_TO_DELETE_AFTER_DEPLOYMENT_27_02 = 10,
  WHATSAPP_WIDGET = 11,
  AI_AGENT = 12,
}

export enum TAB_VALUES {
  ACTIVE = 1,
  INACTIVE = 2,
  ALL = 0,
}

export enum NEWSLETTER_TAB_VALUES {
  SENT = 1,
  SCHEDULED,
  DRAFTS,
  ARCHIVED,
}

export enum WhatsappComponentTypes {
  BODY = "BODY",
  HEADER = "HEADER",
  FOOTER = "FOOTER",
  BUTTONS = "BUTTONS",
}

export enum WhatsappComponentFormat {
  IMAGE = "IMAGE",
  TEXT = "TEXT",
}

export enum WhatsappButtonTypes {
  URL = "URL",
  PHONE_NUMBER = "PHONE_NUMBER",
  COPY_CODE = "COPY_CODE",
  // QUICK_REPLY = "QUICK_REPLY", // we don't support this type for now
  // OTP = "OTP", // we don't support this type for now
}

export enum WhatsappComponentFormats {
  TEXT = "TEXT",
  // IMAGE = "IMAGE", // we don't support this format for now
  // DOCUMENT = "DOCUMENT", // we don't support this format for now
  // VIDEO = "VIDEO", // we don't support this format for now
}

export type Platform =
  | "magento"
  | "magento2"
  | "prestashop"
  | "shoper"
  | "shopgold"
  | "iai"
  | "cscart"
  | "shopify"
  | "shoplo"
  | "shoppub"
  | "opencart"
  | "woocommerce"
  | "shopware"
  | "atomstore"
  | "goshop"
  | "twoclickshop"
  | "ebexo"
  | "sote"
  | "sitecore"
  | "selly"
  | "comarch"
  | "vtex"
  | "vtexCms"
  | "vtexIo"
  | "tray"
  | "spreecommerce"
  | "aptusshop"
  | "esklep"
  | "skyshop"
  | "kqs_store"
  | "trisoshop"
  | "magazord"
  | "selesto"
  | "other"
  | "custom"
  | "universal"
  | "2clickshop"
  | "nuvemshop"
  | "lojaintegrada"
  | "panorama"
  | "jet"
  | "bw_commerce"
  | "irroba"
  | "wbuy";

export enum Platforms {
  Magento = "magento",
  Magento2 = "magento2",
  Prestashop = "prestashop",
  Shoper = "shoper",
  Shopgold = "shopgold",
  IAI = "iai",
  Cscart = "cscart",
  Shopify = "shopify",
  Shoplo = "shoplo",
  Shoppub = "shoppub",
  Opencart = "opencart",
  Woocommerce = "woocommerce",
  Shopware = "shopware",
  Atomstore = "atomstore",
  Goshop = "goshop",
  Twoclickshop = "twoclickshop",
  Ebexo = "ebexo",
  Sote = "sote",
  Sitecore = "sitecore",
  Selly = "selly",
  Comarch = "comarch",
  Vtex = "vtex",
  VtexCms = "vtexCms",
  VtexIo = "vtexIo",
  Tray = "tray",
  Spreecommerce = "spreecommerce",
  Aptusshop = "aptusshop",
  Skyshop = "skyshop",
  KqsStore = "kqs_store",
  Trisoshop = "trisoshop",
  Magazord = "magazord",
  Selesto = "selesto",
  Custom = "custom",
  Nuvemshop = "nuvemshop",
  LojaIntegrada = "lojaintegrada",
  Panorama = "panorama",
  Jet = "jet",
  BwCommerce = "bw_commerce",
  Irroba = "irroba",
  WBuy = "wbuy",
}

export enum EngagementAttributionReasonGroup {
  EMAIL = "EMAIL",
  SMS = "SMS",
  WHATSAPP = "WHATSAPP",
}

export const ENGAGEMENT_ATTRIBUTION_REASON = {
  EMAIL_OPENED: 1,
  EMAIL_CLICKED: 4,
  SMS_DELIVERED: 8,
  SMS_CLICKED: 16,
  WHATSAPP_DELIVERED: 32,
  WHATSAPP_OPENED: 64,
  WHATSAPP_CLICKED: 128,
} as const;

export type ENGAGEMENT_ATTRIBUTION_REASON_ID =
  (typeof ENGAGEMENT_ATTRIBUTION_REASON)[keyof typeof ENGAGEMENT_ATTRIBUTION_REASON];

export enum WHATSAPP_MESSAGE_STATUS {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  DELETED = "DELETED",
}
